import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { COLOR_PALETTE, FORMAT_STYLES } from '../constants';
import { EditorCommandType, RichTextEditorCommandService } from '../rich-text-editor-command.service';

export type ToolbarActionType =
  | 'bold'
  | 'underline'
  | 'strikethrough'
  | 'unorderedList'
  | 'orderedList'
  | 'alignLeft'
  | 'alignCenter'
  | 'alignRight'
  | 'alignJustify'
  | 'formatChange'
  | 'textColor'
  | 'backgroundColor'
  | 'customTextColor'
  | 'customBgColor'
  | 'toggleTextColorPicker'
  | 'toggleBgColorPicker'
  | 'insertLink'
  | 'removeFormat'
  | 'undo'
  | 'redo';

export interface IToolbarAction {
  type: ToolbarActionType;
  data?: any;
}

@Component({
  selector: 'sgt-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToolbarComponent {
  @Input() settings = {
    currentFormat: '',
    showTextColorPicker: false,
    showBgColorPicker: false,
    customTextColor: '#000',
    customBgColor: '#fff',
  }

  @Output() action = new EventEmitter<IToolbarAction>();
  @Output() mousedown = new EventEmitter<MouseEvent>();

  formatStyles = FORMAT_STYLES;
  colorPalette = COLOR_PALETTE;

  constructor(private commandService: RichTextEditorCommandService) {}

  isCommandActive(command: string): boolean {
    return this.commandService.isCommandActive(command as EditorCommandType);
  }

  isAlignmentActive(alignment: string): boolean {
    return this.commandService.isCommandActive(alignment as EditorCommandType);
  }

  private emitAction(type: ToolbarActionType, data?: any): void {
    this.action.emit({ type, data });
  }

  onFormatBold(): void {
    this.emitAction('bold');
  }

  onFormatUnderline(): void {
    this.emitAction('underline');
  }

  onFormatStrikethrough(): void {
    this.emitAction('strikethrough');
  }

  onFormatUnorderedList(): void {
    this.emitAction('unorderedList');
  }

  onFormatOrderedList(): void {
    this.emitAction('orderedList');
  }

  onFormatAlignLeft(): void {
    this.emitAction('alignLeft');
  }

  onFormatAlignCenter(): void {
    this.emitAction('alignCenter');
  }

  onFormatAlignRight(): void {
    this.emitAction('alignRight');
  }

  onFormatAlignJustify(): void {
    this.emitAction('alignJustify');
  }

  onFormatChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const format = select.value;

    if (format) {
      this.emitAction('formatChange', { format });
    }
  }

  onToggleTextColorPicker(): void {
    this.emitAction('toggleTextColorPicker');
  }

  onToggleBgColorPicker(): void {
    this.emitAction('toggleBgColorPicker');
  }

  onApplyTextColor(color: string): void {
    this.emitAction('textColor', { color });
  }

  onApplyBackgroundColor(color: string): void {
    this.emitAction('backgroundColor', { color });
  }

  onCustomTextColorChange(): void {
    this.emitAction('customTextColor', this.settings.customTextColor);
  }

  onCustomBgColorChange(): void {
    this.emitAction('customBgColor', this.settings.customBgColor);
  }

  onInsertLink(): void {
    this.emitAction('insertLink');
  }

  onRemoveFormat(): void {
    this.emitAction('removeFormat');
  }

  onUndo(): void {
    this.emitAction('undo');
  }

  onRedo(): void {
    this.emitAction('redo');
  }

  onMousedown(event: MouseEvent): void {
    this.mousedown.emit(event);
  }
}
